import * as React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import Media from 'react-media';
import { getText, getConfig, debounce, cn } from '../../../helpers';
import { ReactComponent as BookmarkIcon } from '../../../assets/svg/icons/bookmark.svg';
import { ReactComponent as LongBookmarkIcon } from '../../../assets/svg/icons/bookmark-blueprint.svg';

const Window = window as AnonymousObject;
Window.dataLayer = Window.dataLayer || [];

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        tooltip: {
            backgroundColor: '#fff',
            color: 'black',
            maxWidth: theme.typography.pxToRem(450),
            fontSize: theme.typography.pxToRem(14),
            border: '1px solid #ccc',
            fontFamily: 'Akk Pro, Arial, sans-serif'
        }
    });
});

const debounceDuration = getConfig('components.typeaheadThrottleDuration');

function Bookmark(props: BookmarkProps & BookmarkActions & BookmarkContainerProps) {
    const { keyId, isLoggedIn, name, bookmarkTooltip, isDataBookmarked, bookmarkType, updateUserPreference, longBookmarkIcon } = props;
    const [openTooltip, setOpenTooltip] = React.useState(false);
    const [isBookmarked, setBookmarked] = React.useState(false);
    const [disablePointerEvents, setDisablePointerEvents] = React.useState<boolean>(false);
    const bookmarkRef = React.useRef(null);

    React.useEffect(() => {
        setBookmarked(isDataBookmarked);
    }, [isDataBookmarked]);

    const notLoggedInTitle = getText('bookmark.tooltip.notLoggedIn');
    let bookmarkTitle = notLoggedInTitle;
    let bookmarkTitleMobile = notLoggedInTitle;
    if (isLoggedIn) {
        const bookmarkDesktop = bookmarkTooltip && bookmarkTooltip.find(item => item.deviceType === 'desktop');
        const bookmarkMobile = bookmarkTooltip && bookmarkTooltip.find(item => item.deviceType === 'mobile');
        bookmarkTitle = bookmarkDesktop && (isBookmarked ? bookmarkDesktop.bookmarked : bookmarkDesktop.notBookmarked);
        bookmarkTitleMobile = bookmarkMobile && (isBookmarked ? bookmarkMobile.notBookmarked : bookmarkMobile.bookmarked);
    }
    const handleTooltipOpen = () => {
        setOpenTooltip(true);
    };

    const handleTooltipClose = debounce(() => {
        setOpenTooltip(false);
    }, debounceDuration);

    const handleLeave = () => {
        setDisablePointerEvents(false);
    };

    function SwitchBookmark() {
        if (isLoggedIn) {
            let bookmarkAction = '';
            let gaBookmarkAction = '';
            if (isDataBookmarked) {
                bookmarkAction = 'remove';
                gaBookmarkAction = 'removed';
            } else {
                bookmarkAction = 'add';
                gaBookmarkAction = 'added';
            }
            if (isDataBookmarked && bookmarkRef.current) {
                bookmarkRef.current.blur();
                document.body.focus();
                setDisablePointerEvents(true);
            }
            setBookmarked(!isDataBookmarked);
            const bookmarkparams: BookmarkParams = {
                action: bookmarkAction,
                type: bookmarkType,
                bookmark: { keyId, name: bookmarkType === 'queries' ? name : '', created: new Date() },
                onSuccess: () => {
                    Window.dataLayer.push({
                        event: 'formSubmit',
                        submitSuccess: 'true',
                        formType: 'bookmark',
                        bookmarkStatus: gaBookmarkAction,
                        additionalDetails: `${bookmarkType} bookmark ${gaBookmarkAction}: ${keyId}`
                    });
                }
            };

            updateUserPreference(bookmarkparams);
        }
        handleTooltipOpen();
        handleTooltipClose();
    }

    return (
        <>
            <Media query={{ maxWidth: 1023 }}>
                <div
                    data-testid="bookmark-icon"
                    className={cn('', {
                        'bookmark-section__bookmarked': isBookmarked,
                        'bookmark-section__not-bookmark': !isBookmarked
                    })}
                >
                    <Tooltip
                        classes={useStyles(0)}
                        placement="top"
                        open={openTooltip}
                        onClose={handleTooltipClose}
                        arrow
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={bookmarkTitleMobile}
                    >
                        {longBookmarkIcon ? (
                            <LongBookmarkIcon tabIndex={0} onClick={SwitchBookmark} onKeyDown={e => e.key === 'Enter' && SwitchBookmark()} />
                        ) : (
                            <BookmarkIcon tabIndex={0} onClick={SwitchBookmark} onKeyDown={e => e.key === 'Enter' && SwitchBookmark()} />
                        )}
                    </Tooltip>
                </div>
            </Media>
            <Media query={{ minWidth: 1024 }}>
                <div
                    data-testid="bookmark-icon"
                    className={cn('', {
                        'bookmark-section__bookmarked': isBookmarked && !longBookmarkIcon,
                        'bookmark-section__not-bookmark': !isBookmarked && !longBookmarkIcon && !disablePointerEvents,
                        'bookmark-section__not-bookmark-nohover': !isBookmarked && !longBookmarkIcon && disablePointerEvents,
                        'bookmark-section__bookmarked_blueprint': isBookmarked && longBookmarkIcon,
                        'bookmark-section__not-bookmark_blueprint': !isBookmarked && longBookmarkIcon && !disablePointerEvents,
                        'bookmark-section__not-bookmark_blueprint-nohover': !isBookmarked && longBookmarkIcon && disablePointerEvents
                    })}
                >
                    <Tooltip
                        classes={useStyles(0)}
                        placement="top"
                        open={openTooltip}
                        onClose={handleTooltipClose}
                        arrow
                        disableFocusListener={window.matchMedia('(pointer: coarse)').matches}
                        disableTouchListener={window.matchMedia('(pointer: coarse)').matches}
                        title={bookmarkTitle}
                    >
                        {longBookmarkIcon ? (
                            <LongBookmarkIcon
                                ref={bookmarkRef}
                                className={(window.matchMedia('(pointer: coarse)').matches && !isLoggedIn) ? 'svg__device' : ''}
                                onMouseLeave={handleLeave}
                                onMouseEnter={handleTooltipOpen}
                                onFocus={handleTooltipOpen}
                                onBlur={handleTooltipClose}
                                tabIndex={0}
                                onClick={SwitchBookmark}
                                onKeyDown={e => e.key === 'Enter' && SwitchBookmark()}
                            />
                        ) : (
                            <BookmarkIcon
                                ref={bookmarkRef}
                                className={(window.matchMedia('(pointer: coarse)').matches && !isLoggedIn) ? 'svg__device' : ''}
                                onMouseLeave={handleLeave}
                                onMouseEnter={handleTooltipOpen}
                                onFocus={handleTooltipOpen}
                                onBlur={handleTooltipClose}
                                tabIndex={0}
                                onClick={SwitchBookmark}
                                onKeyDown={e => e.key === 'Enter' && SwitchBookmark()}
                            />
                        )}
                    </Tooltip>
                </div>
            </Media>
        </>
    );
}

export default React.memo(Bookmark);
